import { Divider, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, Radio, RadioGroup, Switch, Tooltip } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import AlertModal from 'components/Alert';
import { Formik, Form as FormikForm, Field } from 'formik';
import * as React from 'react';
import { CharacterCounterFormField } from 'v2/components/Shared/FormFields/CharacterCounterFormField';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import TextFormField from 'v2/components/Shared/FormFields/TextFormField';
import { Space } from 'v2/components/Shared/Space';

import { CustomFrequencyType, frequencyOptions, initialValues, FrequencyType } from './constants';
import { Pricing } from './Pricing';
import { useStyles } from './styles';
import { validationSchema } from './validation';

import type { FormProps, FormValues } from './constants';
import type IProduct from 'interfaces/IProduct';

function Form({ product, handleClose, updateProduct }: FormProps) {
  const classes = useStyles();

  const shouldDisableInstallmentFocus = (membershipFee: boolean) => {
    return !(product.trial || membershipFee);
  };

  const shouldDisableMembershipFeeFocus = (yearlyInstallment: boolean) => {
    return !(product.trial || yearlyInstallment);
  };

  function showYearlyInstallment(frequencyType: FrequencyType, frequencyTypeSelect: CustomFrequencyType) {
    if (frequencyType === FrequencyType.MENSAL) return false;
    if (frequencyType === FrequencyType.PERSONALIZADO && frequencyTypeSelect === CustomFrequencyType.MES) return false;
    return true;
  }

  function getEnableInstallmentTitle(product: Partial<IProduct>, currFormValues: FormValues) {
    const { trial } = product;
    const { membership_fee } = currFormValues;
    if (trial) return 'O Parcelamento da Recorrência não pode ser habilitado junto com o Trial';
    if (membership_fee) return 'O Parcelamento da Recorrência não pode ser habilitado junto com a Adesão';
    return '';
  }

  const onSubmit = (values: FormValues) => {
    const brlPrice = values.prices.find((price) => price.currency_code === 'BRL');
    const newProduct = {
      ...product,
      ...values,
      undetermined_charges: values.undetermined_charges === 'true',
      trial_days: values.trial && !values.membership_fee ? values.trial_days : 1,
      overdue_limit: values.remove_access ? values.overdue_limit : 5,
      membership_price: values.membership_fee && !values.trial ? brlPrice?.membership_value || 0 : 0,
      recurring_value: brlPrice?.value || 0,
      price: brlPrice?.value || 0,
    };
    updateProduct(newProduct);
    handleClose();
  };

  function getMembershipFeeTooltipTitle(product: Partial<IProduct>, currFormValues: FormValues) {
    const { trial } = product;
    const { yearly_installment } = currFormValues;

    if (trial) return 'A Adesão não pode ser habilitada junto com o Trial';
    if (yearly_installment) return 'A Adesão não pode ser habilitado junto com o Parcelamento da Recorrência';
    return '';
  }

  async function handleCancel(isDirty: boolean) {
    if (!isDirty) return handleClose();

    const confirm = await AlertModal.confirm({
      message: 'Caso clique em continuar, perderá todas as informações preenchidas. Deseja continuar?',
      buttonOk: 'Continuar',
      small: true,
    });

    if (!confirm) return;

    handleClose();
  }

  return (
    <Formik
      initialValues={{
        ...initialValues,
        ...product,
        recurring_frequency_type: (product.recurring_frequency_type as FrequencyType) ?? initialValues.recurring_frequency_type,
        recurring_frequency_type_select:
          (product.recurring_frequency_type_select as CustomFrequencyType) ?? initialValues.recurring_frequency_type_select,
        undetermined_charges: String(product.undetermined_charges) ?? initialValues.undetermined_charges,
        prices: product.prices?.length > 0 ? product.prices : initialValues.prices,
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, values, dirty: isDirty, handleChange, handleSubmit }) => (
        <FormikForm className={classes.container}>
          <Typography variant='h6' gutterBottom>
            Configurações de pagamento e cobranças do plano
          </Typography>

          <Grid container>
            <Grid xs={12} sm={9}>
              <Field
                id='recurring_frequency_type'
                name='recurring_frequency_type'
                label='Frequência de cobrança'
                fullWidth
                options={frequencyOptions}
                component={SelectFormField}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          {values.recurring_frequency_type === FrequencyType.PERSONALIZADO && (
            <Grid container>
              <Grid xs={3}>
                <Field
                  id='recurring_frequency'
                  name='recurring_frequency'
                  component={TextFormField}
                  label='Repetir cobrança a cada'
                  type='number'
                  maxLength={2}
                />
              </Grid>
              <Grid xs={9}>
                <FormControl component='fieldset' style={{ height: '100%' }}>
                  <FormLabel>&nbsp;</FormLabel>
                  <RadioGroup
                    row
                    aria-label='recurring_frequency_type_select'
                    name='recurring_frequency_type_select'
                    value={values.recurring_frequency_type_select}
                    onChange={handleChange}
                    style={{ paddingLeft: 16, margin: 5 }}
                  >
                    <FormControlLabel id='recurring_frequency_type_month' value={CustomFrequencyType.MES} control={<Radio />} label='Mês(es)' />
                    <FormControlLabel id='recurring_frequency_type_year' value={CustomFrequencyType.ANO} control={<Radio />} label='Ano(s)' />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          )}

          <div>
            <FormControl component='fieldset'>
              <FormLabel>Forma de cobrança</FormLabel>
              <RadioGroup
                row
                aria-label='undetermined_charges'
                name='undetermined_charges'
                value={values.undetermined_charges}
                onChange={handleChange}
              >
                <FormControlLabel
                  value={'true'}
                  control={<Radio id='base-plan-modal-undetermined-charges-true-radio-button' />}
                  label='Cobrança Infinita - Até o cliente cancelar'
                />
                <FormControlLabel
                  value={'false'}
                  control={<Radio id='base-plan-modal-undetermined-charges-false-radio-button' />}
                  label='Cobrança Finita - Número predefinido de cobranças'
                />
              </RadioGroup>
            </FormControl>
          </div>

          {values.undetermined_charges === 'false' && (
            <div>
              <Grid container>
                <Grid xs={6}>
                  <Field
                    id='limit_charges'
                    name='limit_charges'
                    component={TextFormField}
                    label='O contrato se encerra após quantas cobranças?'
                    type='number'
                    maxLength={2}
                    inputProps={{
                      min: 2,
                    }}
                  />
                  <FormHelperText>Digite o número da quantidade de cobranças para ao término do contrato (mínimo 2).</FormHelperText>
                </Grid>
              </Grid>
            </div>
          )}

          <Divider />

          {showYearlyInstallment(values.recurring_frequency_type, values.recurring_frequency_type_select) && (
            <div>
              <Tooltip
                disableFocusListener={shouldDisableInstallmentFocus(values.membership_fee)}
                disableHoverListener={shouldDisableInstallmentFocus(values.membership_fee)}
                disableTouchListener={shouldDisableInstallmentFocus(values.membership_fee)}
                title={getEnableInstallmentTitle(product, values)}
                placement='bottom'
              >
                <>
                  <FormControlLabel
                    label='Ativar parcelamento no Checkout'
                    control={<Switch id='yearly_installment' color='primary' checked={values.yearly_installment} onChange={handleChange} />}
                  />
                  <FormHelperText>Uma vez habilitada, o cliente poderá efetuar o parcelamento da cobrança deste plano.</FormHelperText>
                </>
              </Tooltip>
            </div>
          )}
          <div>
            <FormControlLabel
              label='Cancelar acesso por atraso'
              control={<Switch id='remove_access' color='primary' checked={values.remove_access} onChange={handleChange} />}
            />
            <FormHelperText>Defina a quantidade de parcelas em atraso para a assinatura ser cancelada automaticamente.</FormHelperText>
          </div>

          {values.remove_access && (
            <Grid container>
              <Grid item xs={6}>
                <>
                  <Field id='overdue_limit' name='overdue_limit' component={TextFormField} label='Quantidade de parcelas atrasadas' type='number' />
                  <FormHelperText>
                    {values.overdue_limit > 0 ? `Após ${values.overdue_limit} cobrança(s) em atraso, a assinatura será cancelada.` : ''}
                  </FormHelperText>
                </>
              </Grid>
            </Grid>
          )}

          <div>
            <FormControlLabel
              label='Período trial'
              control={
                <Switch
                  id='trial'
                  color='primary'
                  checked={values.membership_fee ? false : values.trial}
                  onChange={handleChange}
                  disabled={values.membership_fee}
                />
              }
            />
            <FormHelperText>
              Período de avaliação, seu produto ou serviço será disponibilizado gratuitamente antes do cliente tomar uma decisão de compra.
            </FormHelperText>
          </div>

          <div>
            <Tooltip
              disableFocusListener={shouldDisableMembershipFeeFocus(values.yearly_installment)}
              disableHoverListener={shouldDisableMembershipFeeFocus(values.yearly_installment)}
              disableTouchListener={shouldDisableMembershipFeeFocus(values.yearly_installment)}
              title={getMembershipFeeTooltipTitle(product, values)}
              placement='bottom'
            >
              <>
                <FormControlLabel
                  label='Taxa de contratação / adesão'
                  control={
                    <Switch
                      id='membership_fee'
                      color='primary'
                      checked={values.trial ? false : values.membership_fee}
                      onChange={handleChange}
                      disabled={values.trial}
                    />
                  }
                />
                <FormHelperText>Cobrar uma taxa única no momento em que um cliente adquirir um produto.</FormHelperText>
              </>
            </Tooltip>

            {values.membership_fee && (
              <>
                <Space size={20} axis='vertical' />

                <Alert severity='warning'>
                  Ao habilitar a taxa de adesão, não será possível criar <strong>Ofertas Promocionais</strong> para este produto.
                </Alert>
              </>
            )}
          </div>

          {values.trial && (
            <Grid container>
              <Grid item xs={6}>
                <>
                  <Field
                    id='trial_days'
                    name='trial_days'
                    component={TextFormField}
                    label='Quantidade de DIAS que seu cliente poderá testar seu produto'
                    type='number'
                    inputProps={{
                      min: 1,
                    }}
                  />
                  <FormHelperText>{values.trial_days > 0 ? `Após ${values.trial_days} dia(s), iniciaremos a cobrança do plano.` : ''}</FormHelperText>
                </>
              </Grid>
            </Grid>
          )}

          <Divider />

          <Pricing prices={values.prices} />

          <Divider />

          <Typography variant='h6'>Informação do plano</Typography>

          <Field
            id='plan-title'
            name='plan_title'
            component={TextFormField}
            label='Nome do plano'
            placeholder='Ex: Plano semestral III'
            maxLength={100}
          />

          <div>
            <Field
              id='plan-description'
              name='plan_description'
              component={CharacterCounterFormField}
              label='Descrição do plano'
              limits={4000}
              minimum={100}
              placeholder='Ex: Plano semestral, alternativa aos planos de assinatura mensais ou anuais, fornecendo uma duração intermediária.'
            />
            <FormHelperText>A descrição do plano deve deixar claro todos os detalhes do que está sendo entregue ao seu cliente</FormHelperText>
          </div>

          <footer className={classes.footer}>
            <Button id='cancel-plan-button' variant='outlined' onClick={() => handleCancel(isDirty)}>
              Cancelar
            </Button>
            <Button
              id='create-plan-button'
              variant='contained'
              color='primary'
              type='submit'
              onClick={() => handleSubmit()}
              startIcon={isSubmitting && <CircularProgress size={20} color='inherit' />}
            >
              {product.plan_title ? 'Editar plano' : 'Cadastrar plano'}
            </Button>
          </footer>
        </FormikForm>
      )}
    </Formik>
  );
}

export { Form };
